import { Fragment, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Navbar } from '@/components/Navbar';
import { PublicShell } from '@/components/visual/PublicShell';
import { LocaleLink } from '@/components/LocaleLink';
import { useLocale } from '@/hooks/useLocale';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, ArrowLeft, ChevronRight, Calculator, Loader2, Languages } from 'lucide-react';
import { getPostBySlug, getRelatedPosts, getLocalizedPost, BLOG_CATEGORIES, BLOG_AUTHOR } from '@/data/blogData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(dateStr, lang) {
  const localeMap = { en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', nl: 'nl-NL' };
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(localeMap[lang] || 'en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function renderContent(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`b-${i}-${part.slice(2, 14)}`} className="font-semibold text-[#171A1D]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={`t-${i}-${part.slice(0, 12)}`}>{part}</Fragment>;
  });
}

function SectionBlock({ heading, content }) {
  const paragraphs = content.split('\n\n');
  return (
    <section className="mb-8">
      <h2
        className="text-xl font-bold text-[#171A1D] mb-4"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        {heading}
      </h2>
      {paragraphs.map((para, i) => (
        <p key={`${heading.slice(0, 16)}-p${i}`} className="text-[#3A4249] text-[15px] leading-[1.8] mb-4">
          {renderContent(para)}
        </p>
      ))}
    </section>
  );
}

/* ── Reading Progress Bar ─────────────────────────────────── */
function ReadingProgressBar({ articleRef }) {
  const [progress, setProgress] = useState(0);

  const calcProgress = useCallback(() => {
    const el = articleRef.current;
    if (!el) return;
    const { top, height } = el.getBoundingClientRect();
    const articleTop = top + window.scrollY;
    const scrollable = height - window.innerHeight;
    if (scrollable <= 0) { setProgress(100); return; }
    const pct = ((window.scrollY - articleTop) / scrollable) * 100;
    setProgress(Math.min(100, Math.max(0, pct)));
  }, [articleRef]);

  useEffect(() => {
    window.addEventListener('scroll', calcProgress, { passive: true });
    calcProgress();
    return () => window.removeEventListener('scroll', calcProgress);
  }, [calcProgress]);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] h-[3px] bg-transparent pointer-events-none"
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Reading progress"
      data-testid="reading-progress-bar"
    >
      <div
        className="h-full relative"
        style={{
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #78B928 0%, #2867B2 60%, #2867B2 100%)',
          transition: 'width 80ms ease-out',
          boxShadow: progress > 0 ? '0 0 8px rgba(120, 185, 40,0.6)' : 'none',
        }}
      >
        {progress > 0 && progress < 100 && (
          <span
            className="absolute right-0 top-1/2 -translate-y-1/2 w-[10px] h-[10px] rounded-full bg-[#2867B2] shadow-card"
            style={{ boxShadow: '0 0 6px 2px rgba(120, 185, 40,0.7)' }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

function RelatedCard({ post, lang, t }) {
  const localizedPost = getLocalizedPost(post, lang);
  const cat = BLOG_CATEGORIES[post.category];
  return (
    <LocaleLink
      to={`/blog/${post.slug}`}
      className="group block rounded-lg border border-[#DDE1E5] bg-white hover:shadow-raised hover:border-[#527F19]/30 transition-all p-5"
      data-testid={`related-card-${post.slug}`}
    >
      <Badge variant="secondary" className={`text-xs mb-2 ${cat?.color || ''}`}>
        {t('blogData.categories.' + post.category)}
      </Badge>
      <h3 className="font-bold text-sm text-[#171A1D] group-hover:text-[#527F19] transition-colors leading-snug" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {localizedPost.title}
      </h3>
      <span className="flex items-center gap-1 mt-2 text-xs text-[#8A929B]">
        <Clock className="w-3 h-3" /> {post.readingTime} min
      </span>
    </LocaleLink>
  );
}

function LoadingShell() {
  return (
    <div className="min-h-screen bg-[#F5F6F7] text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <Navbar variant="light" />
      <div className="max-w-3xl mx-auto px-6 py-32 flex flex-col items-center justify-center text-[#8A929B]" data-testid="blog-post-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#527F19]" />
        <p className="mt-4 text-sm">Loading article…</p>
      </div>
    </div>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { localePath } = useLocale();
  const { t, i18n } = useTranslation();
  const articleRef = useRef(null);
  useEffect(() => {
    // Add stable test hooks after the existing trusted-content renderer. Content,
    // destinations, sanitization and indexing metadata are not rewritten.
    articleRef.current?.querySelectorAll('[data-testid="blog-post-body"] a').forEach((link, index) => {
      link.setAttribute('data-testid', `article-content-link-${index}`);
    });
  });

  const staticRaw = getPostBySlug(slug);
  const staticPost = staticRaw ? getLocalizedPost(staticRaw, i18n.language) : null;

  // DB-article state. 'static' = served from the local knowledge base; otherwise
  // we fetch the auto-published article from the API.
  const [dbArticle, setDbArticle] = useState(null);
  const [status, setStatus] = useState(staticRaw ? 'static' : 'loading');

  useEffect(() => {
    if (staticRaw) { setStatus('static'); return; }
    let active = true;
    setStatus('loading');
    setDbArticle(null);
    axios.get(`${API}/blog/articles/${slug}`, { params: { lang: i18n.language } })
      .then(res => { if (active) { setDbArticle(res.data); setStatus('found'); } })
      .catch(() => { if (active) setStatus('notfound'); });
    return () => { active = false; };
  }, [slug, staticRaw, i18n.language]);

  const isDb = status === 'found' && !!dbArticle;
  const related = getRelatedPosts(slug, 3);

  const title = staticPost?.title || dbArticle?.title || '';
  const category = staticRaw?.category || dbArticle?.category;
  const readingTime = staticRaw?.readingTime || dbArticle?.reading_time;
  const publishedDate = staticRaw?.publishedDate || dbArticle?.published_date;
  const cat = category ? BLOG_CATEGORIES[category] : null;

  // EN-only SEO meta overrides (technical audit); other languages keep localized copy.
  const useEnMetaOverride = i18n.language === 'en' && !!staticRaw;
  const metaTitleOverride = useEnMetaOverride ? staticRaw.metaTitle : null;
  usePageTitle(metaTitleOverride || (title ? `${title} | ISMS Calculator` : 'Article'));

  // Sanitize the auto-published HTML (defense-in-depth) and drop its leading <h1>,
  // which we render in the page header instead (avoids a duplicate title).
  const sanitizedHtml = useMemo(() => {
    if (!dbArticle?.content_html) return '';
    const clean = DOMPurify.sanitize(dbArticle.content_html, { USE_PROFILES: { html: true } });
    return clean.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '');
  }, [dbArticle]);

  // ── SEO: meta tags, canonical, hreflang, JSON-LD (works for static + db) ──
  useEffect(() => {
    if (status === 'loading' || status === 'notfound') return;
    let seo = null;
    if (staticRaw) {
      const enMeta = i18n.language === 'en';
      seo = {
        title: (enMeta && staticRaw.metaTitle) || staticPost.title,
        excerpt: (enMeta && staticRaw.metaDescription) || staticPost.excerpt,
        publishedDate: staticRaw.publishedDate,
        category: staticRaw.category, slug: staticRaw.slug, jsonLd: null, faqJsonLd: null,
      };
    } else if (dbArticle) {
      seo = {
        title: dbArticle.title, excerpt: dbArticle.excerpt || dbArticle.meta_description || '',
        publishedDate: dbArticle.published_date, category: dbArticle.category, slug: dbArticle.slug,
        jsonLd: dbArticle.json_ld, faqJsonLd: dbArticle.faq_json_ld,
      };
    }
    if (!seo) return;

    const lang = i18n.language;
    const langCodes = { en: 'en-US', de: 'de-DE', es: 'es-ES', fr: 'fr-FR', nl: 'nl-NL' };
    const base = 'https://ismscalculator.com';
    const postUrl = `${base}/${lang}/blog/${seo.slug}`;
    const created = [];

    const upsertMeta = (attr, key, val) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', val);
    };
    const addLink = (attrs) => {
      const el = document.createElement('link');
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      document.head.appendChild(el);
      created.push(el);
    };
    const addJsonLd = (obj) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
      created.push(script);
    };

    upsertMeta('name', 'description', seo.excerpt);
    upsertMeta('name', 'language', langCodes[lang] || 'en-US');
    upsertMeta('property', 'og:type', 'article');
    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.excerpt);
    upsertMeta('property', 'og:url', postUrl);
    upsertMeta('property', 'og:image', `${base}/og-image.png`);
    upsertMeta('property', 'og:image:width', '1200');
    upsertMeta('property', 'og:image:height', '630');
    upsertMeta('property', 'article:published_time', seo.publishedDate);
    upsertMeta('property', 'article:modified_time', seo.publishedDate);
    upsertMeta('property', 'article:author', BLOG_AUTHOR.name);
    upsertMeta('property', 'article:section', seo.category);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.excerpt);
    upsertMeta('name', 'twitter:image', `${base}/og-image.png`);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
      created.push(canonical);
    }
    canonical.href = postUrl;

    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    ['en', 'de', 'es', 'fr', 'nl'].forEach(l =>
      addLink({ rel: 'alternate', hreflang: l, href: `${base}/${l}/blog/${seo.slug}` })
    );
    addLink({ rel: 'alternate', hreflang: 'x-default', href: `${base}/en/blog/${seo.slug}` });

    // Prefer BabyLoveGrowth's own structured data when present, else build BlogPosting.
    addJsonLd(seo.jsonLd || {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: seo.title,
      description: seo.excerpt,
      url: postUrl,
      datePublished: seo.publishedDate,
      dateModified: seo.publishedDate,
      inLanguage: langCodes[lang] || 'en-US',
      author: { '@type': 'Organization', name: BLOG_AUTHOR.name, url: base },
      publisher: {
        '@type': 'Organization',
        name: BLOG_AUTHOR.name,
        url: base,
        logo: { '@type': 'ImageObject', url: `${base}/favicon-192.png` },
      },
      image: { '@type': 'ImageObject', url: `${base}/og-image.png`, width: 1200, height: 630 },
      mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl },
    });
    if (seo.faqJsonLd) addJsonLd(seo.faqJsonLd);

    return () => created.forEach(el => { if (el.parentNode) el.parentNode.removeChild(el); });
  }, [staticRaw, staticPost, dbArticle, status, i18n.language]);

  if (status === 'loading') return <LoadingShell />;
  if (status === 'notfound') return <Navigate to={localePath('/blog')} replace />;

  return (
    <PublicShell>
      <ReadingProgressBar articleRef={articleRef} />

      <main id="main-content" tabIndex={-1}><article ref={articleRef} className="article-reading">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm text-[#8A929B] mb-8" data-testid="blog-breadcrumb">
          <LocaleLink to="/blog" className="hover:text-[#527F19] transition-colors" data-testid="blog-breadcrumb-index">{t('footer.blog')}</LocaleLink>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#66707A] truncate max-w-[300px]">{title}</span>
        </nav>

        {/* Header */}
        <header className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Badge variant="secondary" className={`text-xs font-medium ${cat?.color || ''}`}>
              {t('blogData.categories.' + category)}
            </Badge>
            <span className="flex items-center gap-1 text-xs text-[#8A929B]">
              <Clock className="w-3 h-3" /> {t('blog.minRead', { minutes: readingTime })}
            </span>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            data-testid="blog-post-title"
          >
            {title}
          </h1>
          <div className="mt-4 flex items-center gap-3 text-sm text-[#66707A]">
            <span>{BLOG_AUTHOR.email}</span>
            <span className="text-[#DDE1E5]">|</span>
            <time dateTime={publishedDate}>{formatDate(publishedDate, i18n.language)}</time>
          </div>
        </header>

        {/* Article Body */}
        {isDb ? (
          <>
            <div
              className="bg-white rounded-lg border border-[#DDE1E5] p-8 sm:p-10 mb-4 article-content"
              data-testid="blog-post-body"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
            {dbArticle.is_translation && (
              <p
                className="mb-10 flex items-start gap-2 text-xs text-[#8A929B] leading-relaxed"
                data-testid="ai-translation-note"
              >
                <Languages className="w-3.5 h-3.5 mt-[1px] shrink-0" aria-hidden="true" />
                <span>{t('blog.aiTranslated')}</span>
              </p>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg border border-[#DDE1E5] p-8 sm:p-10 mb-10" data-testid="blog-post-body">
            {staticPost.sections.map((section, i) => (
              <SectionBlock key={section.heading || `section-${i}`} heading={section.heading} content={section.content} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="rounded-lg bg-[#171A1D] p-8 sm:p-10 text-center mb-12" data-testid="blog-cta">
          <h3
            className="text-xl sm:text-2xl font-semibold text-white mb-3"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('blogData.ctaTitle')}
          </h3>
          <p className="text-sm text-[#8A929B] mb-6 max-w-md mx-auto">
            {t('blogData.ctaDesc')}
          </p>
          <Button asChild data-testid="blog-cta-btn">
            <LocaleLink to="/calculator"><Calculator className="w-4 h-4 mr-2" /> {t('homepage.primaryCta')}</LocaleLink>
          </Button>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <div className="mb-12">
            <h3
              className="text-lg font-bold mb-4"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('blogData.relatedArticles')}
            </h3>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="related-articles">
              {related.map(p => (
                <RelatedCard key={p.slug} post={p} lang={i18n.language} t={t} />
              ))}
            </div>
          </div>
        )}

        {/* Back to blog */}
        <LocaleLink to="/blog" className="inline-flex items-center gap-2 text-sm text-[#527F19] hover:underline" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> {t('blogData.backToArticles')}
        </LocaleLink>
      </article></main>
    </PublicShell>
  );
}
